import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { LabelComponent } from "@components/label/label.component";
import { PageComponent } from "@components/page/page.component";
import { SaveButtonComponent } from "@components/save-button/save-button.component";
import { ToggleListItemComponent } from "@components/toggle-list/toggle-list-item/toggle-list-item.component";
import { ToggleListComponent } from "@components/toggle-list/toggle-list.component";
import { TranslatePipe } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { CommandService } from "~app/common/services/cqs/command.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { EventHubService } from "~app/common/services/eventhub.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { CopyLeverantorsorderCommand } from "../commands/CopyLeverantorsorderCommand";
import { CopyLeverantorsorderCompletedEventMessage } from "../events/CopyLeverantorsorderCompletedEventMessage";
import { LeverantorsorderradEditComponent } from "../leverantorsorder-details/leverantorsorderrad-edit/leverantorsorderrad-edit.component";
import { ILeverantorsorderDetail, ILeverantorsorderDetailRad } from "../models/leverantorsorder-detail-model";
import { CopyLeverantorsorderHeaderComponent } from "./copy-leverantorsorder-header/copy-leverantorsorder-header.component";
import { CopyLeverantorsorderHeaderValidation } from "./copy-leverantorsorder-header/copy-leverantorsorder-header.validation";
import { ICopyLeverantorsorderHeader } from "./copy-leverantorsorder.models";
import { CopyLeverantorsorderService } from "./copy-leverantorsorder.service";

@Component({
    selector: "rask-copy-leverantorsorder",
    templateUrl: "./copy-leverantorsorder.component.html",
    styleUrls: ["./copy-leverantorsorder.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PageComponent,
        CopyLeverantorsorderHeaderComponent,
        ToggleListComponent,
        LabelComponent,
        ToggleListItemComponent,
        LeverantorsorderradEditComponent,
        SaveButtonComponent,
        TranslatePipe
    ]
})
export class CopyLeverantorsorderComponent implements OnInit, OnDestroy {
    header: ICopyLeverantorsorderHeader;
    leverantorsorder: ILeverantorsorderDetail;
    leverantorsorderTyp: string;
    rootForm: UntypedFormGroup;
    validationContext: IValidationContext;

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly authService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly commandService = inject(CommandService);
    private readonly copyLeverantorsorderService = inject(CopyLeverantorsorderService);
    private readonly eventHubService = inject(EventHubService);
    private readonly eventService = inject(EventService);
    private readonly modalService = inject(RaskModalService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly router = inject(Router);

    private copyWorkComplete$: Subject<void>;

    get pageTitle(): string {
        return "Leverantörsorder";
    }

    canEdit(): boolean {
        return this.authService.canEdit();
    }

    canSave(): boolean {
        return this.rootForm.dirty &&
            !this.validationContext.hasErrors(ValidationScopes.skapa) &&
            this.leverantorsorder.leverantorsorderrader.some(k => k.kopiera);
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        const snapshot = this.activatedRoute.snapshot;
        const id = +snapshot.paramMap.get("id");

        this.busyService.addWork(this.copyLeverantorsorderService
            .getPreview(id))
            .subscribe(res => {
                this.header = res.header;
                this.leverantorsorder = res.order;
                this.validationContext = CopyLeverantorsorderHeaderValidation.init(this.header);

                this.eventHubService.registerListener(`leverantorsorder/${this.leverantorsorder.id.toString()}`);
            });

        this.rootForm = new UntypedFormGroup({});
        this.setupFormListener();

        this.eventService
            .subscribeEvent<CopyLeverantorsorderCompletedEventMessage>(CopyLeverantorsorderCompletedEventMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(filter(event => event.leverantorsorderId === this.leverantorsorder.id))
            .subscribe(event => {
                // eslint-disable-next-line sonarjs/deprecation
                if (this.copyWorkComplete$ && !this.copyWorkComplete$.isStopped) {
                    this.copyWorkComplete$.next();
                    this.copyWorkComplete$.complete();
                }

                if (ObjectUtils.isNullOrUndefined(event.validationMessage)) {
                    this.router.navigate([
                        "leverantorsorder",
                        event.targetLeverantorsorderId
                    ]);
                } else {
                    this.showValidationMessage(event.validationMessage);
                }
            });
    }

    save = (): void => {
        try {
            this.copyWorkComplete$ = new Subject();
            this.busyService.addWork(this.copyWorkComplete$).subscribe();

            // Reset commands from leverantorsorderrad-edit
            this.commandService.reset();

            const command = new CopyLeverantorsorderCommand((x) => {
                x.leverantorsorderId = this.leverantorsorder.id;
                x.periodFrom = this.header.periodFrom;
                x.periodTom = this.header.periodTom;
                x.leverantorsorderrader = this.leverantorsorder.leverantorsorderrader.filter(k => k.kopiera).map(k => k.id);
            });

            // Detta command körs alltid isolerat - oavsett vad som ligger i stacken hos commandService
            const local = this.commandService.createInstance();
            local.addTransactionCommand(command);
            local.pushCommandQueueTransaction().subscribe({
                error: () => {
                    this.copyWorkComplete$.next();
                    this.copyWorkComplete$.complete();
                }
            });
            // eslint-disable-next-line sonarjs/no-ignored-exceptions
        } catch (e) {
            this.copyWorkComplete$.next();
            this.copyWorkComplete$.complete();
        }
    };

    private setSubFormValue(lor: ILeverantorsorderDetailRad, controlName: string): void {
        const subForm = this.rootForm.get(`levorder${lor.id}`);
        if (subForm) {
            subForm.get(controlName).setValue(lor[controlName] || null);
        }
    }

    private setupFormListener(): void {
        const subFormName = "copy-order-header";
        if (!this.rootForm.contains(subFormName)) {
            setTimeout(() => {
                this.setupFormListener();
            }, 500);
            return;
        }

        this.rootForm.get(subFormName).get("periodFrom").valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((newValue) => {
                this.leverantorsorder.leverantorsorderrader.forEach(x => {
                    x.periodFrom = newValue;
                    this.setSubFormValue(x, "periodFrom");
                });
            });

        this.rootForm.get(subFormName).get("periodTom").valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((newValue) => {
                this.leverantorsorder.leverantorsorderrader.forEach(x => {
                    x.periodTom = newValue;
                    this.setSubFormValue(x, "periodTom");
                });
            });
    }

    private showValidationMessage(message: string): void {
        const msg = {
            canForce: false,
            isHandled: false,
            validationMessages: [
                { severity: 2,
                    message: message }
            ]
        };
        this.modalService.error("Fel", msg as any).subscribe();
    }
}
